<template>
  <div v-if="open" class="cm-backdrop" @mousedown.self="close">
    <div class="cm-modal" role="dialog" aria-modal="true" aria-label="Compose email">
      <header class="cm-head">
        <div>
          <h3><CygnusIcon name="envelope" /> Send an email</h3>
          <p class="cm-sub">One message to this contact. It sends through the same machinery as your campaigns — tracked, opt-out honoured, and paced by your send window.</p>
        </div>
        <button class="cm-x" title="Close" @click="close"><CygnusIcon name="x" /></button>
      </header>

      <!-- Sent confirmation -->
      <div v-if="sent" class="cm-body">
        <div class="cm-sent">
          <div class="cm-sent__ic"><CygnusIcon name="check-circle" /></div>
          <div class="cm-sent__t">Queued for {{ form.to_email }}</div>
          <p class="cm-sent__m">{{ sentMessage }}</p>
          <div class="cm-actions">
            <button class="cm-btn cm-btn-primary" @click="close">Done</button>
          </div>
        </div>
      </div>

      <!-- Compose form -->
      <div v-else class="cm-body">
        <div v-if="error" class="cm-alert cm-alert--err"><CygnusIcon name="warning" /> {{ error }}</div>
        <div v-if="loadingOptions" class="cm-loading">Loading your senders…</div>

        <template v-else>
          <label class="cm-field">
            <span class="cm-label">To</span>
            <input v-model="form.to_email" type="email" class="cm-input" placeholder="name@company.com" :disabled="busy">
          </label>

          <label class="cm-field">
            <span class="cm-label">From</span>
            <select v-model="form.sender_identity_id" class="cm-input" :disabled="busy || !senders.length">
              <option v-if="!senders.length" :value="0">No sender identity configured</option>
              <option v-for="s in senders" :key="s.id" :value="s.id">
                {{ s.from_name }} &lt;{{ s.from_email }}&gt;{{ s.is_default ? ' · default' : '' }}
              </option>
            </select>
            <span v-if="!senders.length" class="cm-hint cm-hint--warn">
              You need a verified sender first — set one up under <a href="/admin/senders">Senders</a>.
            </span>
          </label>

          <label v-if="templates.length" class="cm-field">
            <span class="cm-label">Start from a template <span class="cm-optional">optional</span></span>
            <select v-model="form.template_id" class="cm-input" :disabled="busy" @change="onTemplateChange">
              <option :value="0">— Write it myself —</option>
              <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.title }}{{ t.is_host ? ' (default)' : '' }}</option>
            </select>
            <span v-if="form.template_id" class="cm-hint">The template's content is used as the body; the subject defaults to the template title (edit it below).</span>
          </label>

          <!-- Draft with AI — teal (AI canon), only when we have a prospect to ground
               the opener and the body editor is in play. Fills the editable fields;
               nothing sends. -->
          <div v-if="form.prospect_id && !form.template_id" class="cm-ai">
            <button type="button" class="cm-ai-btn" :disabled="busy || drafting" @click="draftWithAi">
              <CygnusIcon name="spinner" :spin="true" v-if="drafting" />
              <CygnusIcon name="bolt" v-else />
              <span>{{ drafting ? "Drafting…" : (form.subject || form.body ? "Redraft with AI" : "Draft with AI") }}</span>
            </button>
            <span class="cm-ai-note">Fills the subject &amp; message below from this contact's context. Read it, make it yours, and check the facts — nothing sends on its own.</span>
          </div>
          <div v-if="draftQuota" class="cm-alert cm-alert--warn">
            <CygnusIcon name="pause" />
            <span>You're out of AI tokens this period, so no opener was drafted — nothing was charged. <router-link to="/admin/usage-plans">Top up</router-link> to draft openers again.</span>
          </div>
          <div v-else-if="draftError" class="cm-alert cm-alert--warn"><CygnusIcon name="warning" /> {{ draftError }}</div>

          <label class="cm-field">
            <span class="cm-label">Subject</span>
            <input v-model="form.subject" type="text" class="cm-input" :placeholder="form.template_id ? 'Defaults to the template title' : 'Your website'" :disabled="busy">
          </label>

          <label v-if="!form.template_id" class="cm-field">
            <span class="cm-label">Message</span>
            <textarea v-model="form.body" class="cm-input cm-textarea" rows="9" placeholder="Write your message…" :disabled="busy"></textarea>
            <span class="cm-hint">Plain text is fine — line breaks become paragraphs. This person is added to your contacts so the reply lands on their timeline.</span>
          </label>

          <p class="cm-hint cm-unsub-hint">This email carries a one-click unsubscribe link for this person. Put {unsubscribe_url} where you want the link to appear; if the message has none, a short unsubscribe line is added at the end.</p>

          <div class="cm-actions">
            <button class="cm-btn cm-btn-ghost" :disabled="busy" @click="close">Cancel</button>
            <button class="cm-btn cm-btn-primary" :disabled="busy || !canSend" @click="submit">
              <CygnusIcon name="spinner" :spin="true" v-if="busy" />
              <span v-else><CygnusIcon name="envelope" /> Queue email</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * MusubiComposeModal — the per-contact COMPOSE surface (follow-up-pool slice A).
 * A reusable modal embedded on the prospect page, the reply inbox (wrong-person
 * "Start new conversation"), and the subscribers surface. Tech alerts deep-link
 * the prospect page with a compose intent, which opens this.
 *
 * Sends through POST /api/v1/admin/compose — the STANDARD outbound machinery
 * (metered, tracked, List-Unsubscribe enforced, suppression/opt-out gated,
 * queue-don't-send).
 *
 * AI Openers (pool item): when a prospect grounds the compose, a teal "Draft with
 * AI" button prefills the editable subject/body via POST /compose/draft-opener.
 * The draft is ALWAYS editable and NOTHING sends on its own — a human reviews,
 * edits and hits Queue. A caller may also pass a ready `body` (e.g. the tech-alerts
 * "Use this draft") + a `signal_id` to ground an in-modal redraft.
 *
 * Controlled by the parent: `open` (bool) + `context` ({ to_email, to_name,
 * prospect_id, subscriber_id, subject, body, signal_id }). Emits `close` and
 * `sent` (with the server payload) so the parent can refresh a timeline.
 */
export default {
  name: "MusubiComposeModal",
  props: {
    open: { type: Boolean, default: false },
    context: { type: Object, default: () => ({}) },
  },
  emits: ["close", "sent"],
  data() {
    return {
      api: useApi(),
      loadingOptions: false,
      busy: false,
      error: null,
      sent: false,
      sentMessage: "",
      senders: [],
      templates: [],
      drafting: false,
      draftError: null,
      draftQuota: false,
      signalId: 0,
      form: { to_email: "", to_name: "", subject: "", body: "", template_id: 0, sender_identity_id: 0, prospect_id: 0, subscriber_id: 0 },
    };
  },
  computed: {
    canSend() {
      if (!this.form.to_email) return false;
      if (this.form.template_id) return true; // template supplies the body + a default subject
      return !!(this.form.subject && this.form.body);
    },
  },
  watch: {
    open(v) {
      if (v) this.onOpen();
    },
  },
  methods: {
    async onOpen() {
      this.error = null;
      this.draftError = null;
      this.draftQuota = false;
      this.sent = false;
      this.busy = false;
      const c = this.context || {};
      this.signalId = Number(c.signal_id || 0);
      this.form = {
        to_email: c.to_email || "",
        to_name: c.to_name || "",
        subject: c.subject || "",
        // A caller may hand us a ready draft body (e.g. the tech-alerts "Use this
        // draft"); it stays fully editable. Body only renders when template_id === 0.
        body: c.body || "",
        template_id: 0,
        sender_identity_id: 0,
        prospect_id: Number(c.prospect_id || 0),
        subscriber_id: Number(c.subscriber_id || 0),
      };
      await this.loadOptions();
    },
    async draftWithAi() {
      if (this.drafting || this.busy) return;
      this.drafting = true;
      this.draftError = null;
      this.draftQuota = false;
      try {
        const payload = {
          prospect_id: this.form.prospect_id || 0,
          subscriber_id: this.form.subscriber_id || 0,
        };
        if (this.signalId) payload.signal_id = this.signalId;
        const res = await this.api.post("/api/v1/admin/compose/draft-opener", payload);
        const body = res?.data ?? res;
        if (body?.success) {
          this.form.template_id = 0; // keep the body editor visible for the draft
          if (body.subject) this.form.subject = body.subject;
          if (body.body) this.form.body = body.body;
        } else if (body?.quota_exhausted) {
          this.draftQuota = true;
        } else {
          this.draftError = body?.error || "Could not draft an opener just now.";
        }
      } catch (e) {
        this.draftError = this.errText(e, "Could not draft an opener just now.");
      } finally {
        this.drafting = false;
      }
    },
    async loadOptions() {
      this.loadingOptions = true;
      try {
        const res = await this.api.get("/api/v1/admin/compose/options");
        const body = res?.data ?? res;
        this.senders = Array.isArray(body?.senders) ? body.senders : [];
        this.templates = Array.isArray(body?.templates) ? body.templates : [];
        const def = this.senders.find((s) => s.is_default) || this.senders[0];
        if (def && !this.form.sender_identity_id) this.form.sender_identity_id = def.id;
      } catch (e) {
        this.error = this.errText(e, "Could not load your sender identities.");
      } finally {
        this.loadingOptions = false;
      }
    },
    onTemplateChange() {
      // Picking a template clears a hand-written body (the template supplies it);
      // clearing the picker hands the editor back.
      if (this.form.template_id) this.form.body = "";
    },
    async submit() {
      if (!this.canSend || this.busy) return;
      this.busy = true;
      this.error = null;
      try {
        const payload = {
          to_email: this.form.to_email,
          to_name: this.form.to_name || null,
          subject: this.form.subject || "",
          body: this.form.body || "",
          template_id: this.form.template_id || 0,
          sender_identity_id: this.form.sender_identity_id || 0,
          prospect_id: this.form.prospect_id || 0,
          subscriber_id: this.form.subscriber_id || 0,
        };
        const res = await this.api.post("/api/v1/admin/compose", payload);
        const body = res?.data ?? res;
        if (body?.success) {
          this.sent = true;
          this.sentMessage = body.message || "Queued — it will send on the next queue tick.";
          this.$emit("sent", body);
        } else {
          this.error = body?.error || "Could not queue the email.";
        }
      } catch (e) {
        // 422 blocks (suppressed / unsubscribed / no sender) carry an honest message.
        this.error = this.errText(e, "Could not queue the email.");
      } finally {
        this.busy = false;
      }
    },
    close() {
      if (this.busy) return;
      this.$emit("close");
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      return body?.error || body?.message || e?.message || fallback;
    },
  },
};
</script>

<style scoped>
.cm-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); display: flex; align-items: flex-start; justify-content: center; z-index: 1000; padding: 6vh 16px 16px; overflow-y: auto; }
.cm-modal { background: var(--m-bg-card, #fff); border: 1px solid var(--m-border, #E2E8F0); border-radius: 14px; width: 100%; max-width: 560px; box-shadow: var(--m-shadow-panel, 0 10px 40px rgba(0,0,0,0.18)); overflow: hidden; }
.cm-head { display: flex; align-items: flex-start; gap: 12px; padding: 18px 20px; border-bottom: 1px solid var(--m-border, #E2E8F0); }
.cm-head h3 { margin: 0; font-size: 1.05rem; font-weight: 700; display: flex; align-items: center; gap: 9px; color: var(--m-text-primary, #0F172A); }
.cm-head h3 i { color: var(--m-accent, #4F46E5); }
.cm-sub { margin: 5px 0 0; font-size: 0.8rem; color: var(--m-text-secondary, #475569); line-height: 1.5; }
.cm-x { margin-left: auto; background: none; border: none; color: var(--m-text-tertiary, #94A3B8); font-size: 1.05rem; cursor: pointer; padding: 4px 6px; border-radius: 6px; }
.cm-x:hover { background: var(--m-rail-hover-bg, #F1F5F9); color: var(--m-text-primary, #0F172A); }
.cm-body { padding: 18px 20px 20px; }
.cm-loading { padding: 24px; text-align: center; color: var(--m-text-secondary, #475569); }

.cm-field { display: block; margin-bottom: 14px; }
.cm-label { display: block; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700; color: var(--m-text-tertiary, #64748B); margin-bottom: 5px; }
.cm-optional { text-transform: none; letter-spacing: 0; font-weight: 500; color: var(--m-text-tertiary, #94A3B8); margin-left: 6px; }
.cm-input { width: 100%; border: 1px solid var(--m-border, #CBD5E1); background: var(--m-bg-content, #fff); border-radius: 8px; font-size: 0.88rem; padding: 8px 11px; color: var(--m-text-primary, #0F172A); font-family: inherit; box-sizing: border-box; }
.cm-input:focus { outline: none; border-color: var(--m-accent, #4F46E5); box-shadow: 0 0 0 3px var(--m-accent-soft, #EEF0FF); }
.cm-input:disabled { background: var(--m-bg-subtle, #F1F5F9); opacity: 0.7; }
.cm-textarea { resize: vertical; line-height: 1.55; min-height: 140px; }
.cm-hint { display: block; margin-top: 5px; font-size: 0.74rem; color: var(--m-text-tertiary, #94A3B8); line-height: 1.45; }
.cm-hint--warn { color: var(--m-status-warning, #B45309); }
.cm-hint a, .cm-hint--warn a { color: var(--m-accent-hover, #4338CA); }

.cm-alert { display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; border-radius: 8px; font-size: 0.82rem; line-height: 1.45; margin-bottom: 14px; }
.cm-alert--err { background: #FEF2F2; border: 1px solid #FBD5D5; color: #B42318; }
.cm-alert--warn { background: #FFF7ED; border: 1px solid #FED7AA; color: #9A5B12; }
.cm-alert--warn a { color: var(--m-teal-text, #0F766E); font-weight: 700; }
.cm-alert i { margin-top: 1px; flex: none; }

/* Draft with AI — teal AI identity, never the indigo commit colour (canon). */
.cm-ai { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.cm-ai-btn { font-family: inherit; font-weight: 600; font-size: 0.82rem; padding: 0.42rem 0.9rem; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; color: var(--m-teal-text, #0F766E); background: var(--m-teal-soft, #E4F7F4); border: 1px solid var(--m-teal-line, #B6EBE4); white-space: nowrap; }
.cm-ai-btn:hover:not(:disabled) { background: var(--m-teal, #14B8A6); border-color: var(--m-teal, #14B8A6); color: #fff; }
.cm-ai-btn:disabled { opacity: 0.6; cursor: default; }
.cm-ai-note { flex: 1 1 220px; min-width: 200px; font-size: 0.72rem; color: var(--m-text-tertiary, #94A3B8); line-height: 1.4; }

.cm-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 6px; }
.cm-btn { font-family: inherit; font-weight: 600; font-size: 0.85rem; padding: 0.5rem 1rem; border-radius: 8px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.cm-btn:disabled { opacity: 0.5; cursor: default; }
.cm-btn-primary { background: var(--m-accent, #4F46E5); border-color: var(--m-accent, #4F46E5); color: #fff; }
.cm-btn-primary:hover:not(:disabled) { background: var(--m-accent-hover, #4338CA); }
.cm-btn-ghost { background: none; color: var(--m-text-secondary, #475569); border-color: var(--m-border, #CBD5E1); }
.cm-btn-ghost:hover:not(:disabled) { background: var(--m-rail-hover-bg, #F1F5F9); }

.cm-sent { text-align: center; padding: 18px 10px 8px; }
.cm-sent__ic { width: 56px; height: 56px; border-radius: 50%; background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); display: inline-flex; align-items: center; justify-content: center; font-size: 1.5rem; margin-bottom: 12px; }
.cm-sent__t { font-weight: 700; font-size: 1rem; color: var(--m-text-primary, #0F172A); }
.cm-sent__m { font-size: 0.85rem; color: var(--m-text-secondary, #475569); margin: 6px 0 16px; line-height: 1.5; }
</style>
